import { useEffect } from 'react';
import { useAudio } from '@/audio/AudioProvider';
import { useMic } from '@/audio/MicProvider';
import { useSession } from '@/app/session-context';
import { router } from '@/app/router';
import { useUiStore } from '@/stores/ui';
import {
  applySystemBars,
  configureAudio,
  hideSplash,
  installBackButton,
  isNative,
  onAppStateChange,
  onAppUrlOpen,
  onAudioInterruption,
} from './platform';
import { replanReminders } from './notifications';

/**
 * What the app does differently inside a native shell (plan doc 17).
 *
 * Renders nothing. It sits inside the providers so it can reach the microphone
 * and the audio engine, and mounts only once the session has loaded — which is
 * also the right moment to take the splash screen down, because the first
 * thing behind it is the path rather than a spinner.
 *
 * Every hook here is a no-op on the web; see `platform.ts`.
 */
export function NativeBridge() {
  const { engine: micEngine } = useMic();
  const { engine: audioEngine } = useAudio();
  const { profile } = useSession();
  const theme = useUiStore((s) => s.theme);

  useEffect(() => {
    void hideSplash();
  }, []);

  useEffect(() => installBackButton(), []);

  useEffect(() => onAppUrlOpen((path) => void router.navigate(path)), []);

  useEffect(() => {
    const resolved =
      theme === 'system'
        ? window.matchMedia?.('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light'
        : theme;
    void applySystemBars(resolved);
  }, [theme]);

  // Backgrounded: let go of the microphone and stop the sound. The recording
  // indicator would otherwise stay lit, and iOS suspends the audio graph
  // anyway. The exercise asks for the microphone again when it needs it.
  useEffect(
    () =>
      onAppStateChange((active) => {
        if (active) {
          void audioEngine.ensureStarted();
          return;
        }
        micEngine.stop();
        audioEngine.stopAll();
        void configureAudio('playback');
      }),
    [micEngine, audioEngine],
  );

  // A call takes the audio hardware; when it hands it back, the context has to
  // be resumed by us — the browser will not do it on its own.
  useEffect(
    () =>
      onAudioInterruption(({ began }) => {
        if (began) {
          micEngine.stop();
          audioEngine.stopAll();
        } else {
          void audioEngine.ensureStarted();
        }
      }),
    [micEngine, audioEngine],
  );

  // Reminders are handed to the OS a week at a time and re-planned whenever
  // the inputs move: on boot, on every return to the foreground (a session
  // may have happened), and when the hour or the streak changes.
  const reminderHour = profile.reminderHour;
  const streak = profile.streakCurrent;
  useEffect(() => {
    if (!isNative()) return;
    const replan = (): void => void replanReminders({ reminderHour, streakCurrent: streak });
    replan();
    return onAppStateChange((active) => {
      if (active) replan();
    });
  }, [reminderHour, streak]);

  return null;
}
